import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
  Home, FileText, BookOpen, BarChart3, Users, AlertTriangle, Heart, Menu, X,
  ChevronDown, Check, Shield, UserCog, Headphones, LogOut, Phone, GraduationCap
} from 'lucide-react';
import './App.css';

import LoginPage from './pages/LoginPage';
import HomeDashboard from './pages/HomeDashboard';
import CallHandlerPage from './pages/CallHandlerPage';
import ProceduresLibrary from './pages/ProceduresLibrary';
import ReferenceDocuments from './pages/ReferenceDocuments';
import KPIDashboard from './pages/KPIDashboard';
import PeopleRoles from './pages/PeopleRoles';
import IncidentRegister from './pages/IncidentRegister';
import VisionValues from './pages/VisionValues';
import RecruitmentHub from './pages/RecruitmentHub';
import PublicApply from './pages/PublicApply';
import MyTraining from './pages/MyTraining';
import { NotificationProvider, NotificationBell, useNotifications } from './components/NotificationCenter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Role Context
const RoleContext = createContext();
export const useRole = () => useContext(RoleContext);
export { BACKEND_URL };

const ROLES = [
  { key: 'Director', label: 'Director', icon: Shield, color: 'hsl(14, 55%, 45%)' },
  { key: 'Operations Manager', label: 'Operations Manager', icon: UserCog, color: 'hsl(205, 70%, 40%)' },
  { key: 'Client Care Representative', label: 'Client Care Rep', icon: Headphones, color: 'hsl(142, 34%, 34%)' },
];

const NAV_ITEMS = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/call-handler', label: 'Call Handler', icon: Phone },
  { path: '/procedures', label: 'Procedures', icon: FileText },
  { path: '/reference', label: 'Reference Docs', icon: BookOpen },
  { path: '/kpis', label: 'KPI Dashboard', icon: BarChart3 },
  { path: '/people', label: 'People & Roles', icon: Users },
  { path: '/incidents', label: 'Incidents', icon: AlertTriangle },
  { path: '/vision', label: 'Vision & Values', icon: Heart },
  { path: '/recruitment', label: 'Recruitment', icon: UserCog, requireWrite: true },
  { path: '/my-training', label: 'My Training', icon: GraduationCap, requireWrite: true },
];

const MOBILE_NAV = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/call-handler', label: 'Calls', icon: Phone },
  { path: '/procedures', label: 'Procedures', icon: FileText },
  { path: '/kpis', label: 'KPIs', icon: BarChart3 },
  { path: '/incidents', label: 'Incidents', icon: AlertTriangle },
];

function RoleSelector({ role, setRole, variant = 'sidebar' }) {
  const [open, setOpen] = useState(false);
  const currentRole = ROLES.find(r => r.key === role);
  const isMobile = variant === 'mobile';

  return (
    <div className="relative" data-testid="role-selector">
      <button
        onClick={() => setOpen(!open)}
        data-testid="role-selector-button"
        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium ${isMobile ? 'bg-muted text-foreground' : ''}`}
        style={!isMobile ? { background: 'hsl(var(--sidebar-hover-bg))', color: 'hsl(var(--sidebar-fg))' } : {}}
      >
        {currentRole && <currentRole.icon size={16} />}
        <span>{currentRole?.label || role}</span>
        <ChevronDown size={14} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute w-64 rounded-xl p-2 z-[60] ${isMobile ? 'right-0 top-full mt-2' : 'left-0 bottom-full mb-2'}`}
          style={{ background: 'hsl(var(--card))', boxShadow: 'var(--shadow-panel)', color: 'hsl(var(--foreground))' }}
          data-testid="role-selector-popover"
        >
          <div className="text-xs font-semibold px-3 py-2 text-muted-foreground uppercase tracking-wider">Switch Role</div>
          {ROLES.map(r => (
            <button
              key={r.key}
              data-testid={`role-selector-option-${r.key.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => { setRole(r.key); setOpen(false); }}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-left"
              style={{
                background: role === r.key ? 'hsl(var(--accent))' : 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (role !== r.key) e.target.style.background = 'hsl(var(--muted))'; }}
              onMouseLeave={e => { if (role !== r.key) e.target.style.background = 'transparent'; }}
            >
              <r.icon size={18} style={{ color: r.color }} />
              <span className="flex-1">{r.label}</span>
              {role === r.key && <Check size={16} style={{ color: r.color }} />}
            </button>
          ))}
        </div>
      )}
      {open && <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} />}
    </div>
  );
}

function Sidebar({ role, authName, onLogout, isDirector, viewAsRole, setViewAsRole }) {
  const location = useLocation();
  const currentRole = ROLES.find(r => r.key === role);
  const { unread } = useNotifications();

  return (
    <aside className="sidebar" data-testid="sidebar">
      <Link to="/" className="block px-6 py-5">
        <img src="https://customer-assets.emergentagent.com/job_staff-ops-portal/artifacts/jqjh0r3w_Logo%20Pure%20Souls%20%20svg.svg" alt="Pure Souls" className="h-10 brightness-0 invert" data-testid="sidebar-logo" />
      </Link>
      <nav className="flex-1 py-2" aria-label="Main navigation">
        {NAV_ITEMS.filter(item => !item.requireWrite || (role === 'Director' || role === 'Operations Manager')).map(item => (
          <Link key={item.path} to={item.path} className={`nav-item ${location.pathname === item.path ? 'active' : ''}`} data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}-link`} aria-current={location.pathname === item.path ? 'page' : undefined}>
            <item.icon size={20} aria-hidden="true" /><span>{item.label}</span>
            {item.path === '/recruitment' && unread > 0 && (
              <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: 'hsl(14, 70%, 50%)' }} data-testid="recruitment-nav-badge">{unread > 9 ? '9+' : unread}</span>
            )}
          </Link>
        ))}
      </nav>
      <div className="px-4 py-4 border-t" style={{ borderColor: 'hsl(var(--sidebar-hover-bg))' }}>
        <div className="flex items-center gap-3 mb-3">
          {currentRole && <currentRole.icon size={16} style={{ color: currentRole.color }} />}
          <div>
            <p className="text-xs font-semibold" style={{ color: 'hsl(var(--sidebar-fg))' }}>{authName || 'Staff'}</p>
            <p className="text-xs" style={{ color: 'hsl(var(--sidebar-muted-fg))' }}>{currentRole?.label || role}{viewAsRole ? ' (preview)' : ''}</p>
          </div>
          {(role === 'Director' || role === 'Operations Manager') && (
            <div className="ml-auto"><NotificationBell variant="sidebar" /></div>
          )}
        </div>
        {isDirector && (
          <div className="mb-3">
            <p className="text-xs mb-1.5" style={{ color: 'hsl(var(--sidebar-muted-fg))' }}>View as:</p>
            <div className="flex flex-col gap-1">
              {ROLES.map(r => (
                <button key={r.key} onClick={() => setViewAsRole(viewAsRole === r.key ? null : r.key)}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium text-left transition-colors"
                  style={{ background: role === r.key ? 'hsl(var(--sidebar-active-bg))' : 'transparent', color: role === r.key ? 'hsl(var(--sidebar-fg))' : 'hsl(var(--sidebar-muted-fg))' }}
                  data-testid={`view-as-${r.key.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <r.icon size={14} style={{ color: r.color }} /><span>{r.label}</span>
                  {role === r.key && <Check size={12} style={{ marginLeft: 'auto' }} />}
                </button>
              ))}
            </div>
          </div>
        )}
        <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium w-full hover:opacity-80" style={{ color: 'hsl(var(--sidebar-muted-fg))' }} data-testid="logout-button">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </aside>
  );
}

function MobileTopBar({ role, authName, onLogout, isDirector, viewAsRole, setViewAsRole }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const currentPage = NAV_ITEMS.find(item => item.path === location.pathname);

  return (
    <>
      <header className="mobile-top-bar" data-testid="mobile-top-bar">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_staff-ops-portal/artifacts/jqjh0r3w_Logo%20Pure%20Souls%20%20svg.svg"
            alt="Pure Souls"
            className="h-7"
          />
          <span className="text-sm font-semibold">{currentPage?.label || 'Home'}</span>
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">{authName || role}</span>
          {(role === 'Director' || role === 'Operations Manager') && <NotificationBell variant="mobile" />}
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-lg hover:bg-muted" data-testid="mobile-menu-button">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>
      
      {/* Mobile Menu Drawer */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[45]" onClick={() => setMenuOpen(false)} />
          <div className="fixed top-14 right-0 w-64 bg-card rounded-bl-2xl shadow-[var(--shadow-panel)] z-[46] p-3" data-testid="mobile-menu-drawer">
            {NAV_ITEMS.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${location.pathname === item.path ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function BottomTabBar() {
  const location = useLocation();

  return (
    <nav className="bottom-tab-bar" data-testid="bottom-tab-bar">
      {MOBILE_NAV.map(item => (
        <Link
          key={item.path}
          to={item.path}
          className={`bottom-tab-item ${location.pathname === item.path ? 'active' : ''}`}
        >
          <item.icon size={20} />
          <span>{item.label}</span>
        </Link>
      ))}
    </nav>
  );
}

function AppShell({ authRole, authName }) {
  const isDirector = authRole === 'Director';
  const [viewAsRole, setViewAsRole] = useState(null);
  
  // Director can "view as" other roles for preview. Others see only their own role.
  const role = (isDirector && viewAsRole) ? viewAsRole : (authRole || 'Client Care Representative');
  const canWrite = role === 'Director' || role === 'Operations Manager';

  const handleLogout = () => {
    localStorage.removeItem('ps_token');
    window.location.reload();
  };

  return (
    <RoleContext.Provider value={{ role, canWrite, BACKEND_URL }}>
      <NotificationProvider enabled={canWrite}>
        <div className="app-shell">
          <Sidebar role={role} authName={authName} onLogout={handleLogout} isDirector={isDirector} viewAsRole={viewAsRole} setViewAsRole={setViewAsRole} />
          <MobileTopBar role={role} authName={authName} onLogout={handleLogout} isDirector={isDirector} viewAsRole={viewAsRole} setViewAsRole={setViewAsRole} />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<HomeDashboard />} />
              <Route path="/call-handler" element={<CallHandlerPage />} />
              <Route path="/procedures" element={<ProceduresLibrary />} />
              <Route path="/reference" element={<ReferenceDocuments />} />
              <Route path="/kpis" element={<KPIDashboard />} />
              <Route path="/people" element={<PeopleRoles />} />
              <Route path="/incidents" element={<IncidentRegister />} />
              <Route path="/vision" element={<VisionValues />} />
              <Route path="/recruitment" element={<RecruitmentHub />} />
              <Route path="/my-training" element={<MyTraining />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
          <BottomTabBar />
        </div>
        <Toaster position="bottom-right" richColors />
      </NotificationProvider>
    </RoleContext.Provider>
  );
}

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem('ps_token'));
  const [checking, setChecking] = useState(true);
  const [authData, setAuthData] = useState({ role: null, name: null });

  useEffect(() => {
    if (token) {
      fetch(`${BACKEND_URL}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(data => { setAuthData({ role: data.role, name: data.name }); setChecking(false); })
        .catch(() => { localStorage.removeItem('ps_token'); setToken(null); setChecking(false); });
    } else {
      setChecking(false);
    }
  }, [token]);

  const handleLogin = (t, loginData) => {
    setToken(t);
    if (loginData) setAuthData({ role: loginData.role, name: loginData.name });
  };

  if (checking) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'hsl(36, 33%, 96%)' }}>
        <div className="h-8 w-32 bg-muted rounded animate-skeleton" />
      </div>
    );
  }

  // Public apply page — accessible without login
  if (typeof window !== 'undefined' && window.location.pathname === '/apply') {
    return (
      <Router>
        <Routes>
          <Route path="/apply" element={<PublicApply />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </Router>
    );
  }

  if (!token) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="bottom-right" richColors />
      </>
    );
  }

  return (
    <Router>
      <AppShell authRole={authData.role} authName={authData.name} />
    </Router>
  );
}
